import React, { useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import SheetStep from "../SheetStep";
import FmvSection from "./FmvSection";
import { Form, Format, getAuthClaim } from "@redriver/cinnamon";
import {
  getSheetRestrictionsState,
  getSheetRatesState,
  getSheetIpsRatesState,
  getCalculationData,
  getSheetState,
  SheetTypeDropdownLookup,
  getSheetDataState,
} from "features/Sheets";
import {
  FixedInput,
  FixedInputResponsiveLabel,
  FixedString,
} from "components/forms";
import {
  PermissionCheck,
  withCurrentUser,
  withPermissions,
} from "features/../../../shared/components/auth";
import { Targets, Actions } from "constants/permissions";
import {
  paymentMethods,
  SheetViewType,
  FunderType,
  UserType,
  SheetType,
} from "features/../../../shared/constants/enums";
import { useTranslation } from "react-i18next";

const ScheduleSetupStep = ({
  sheetId,
  onNextStep,
  onPreviousStep,
  displayPrevious,
  displayNext,
  displaySave,
  collapsed,
  toggleVisibility,
  reactRef,
  calculating,
  sheetDetailsSaving,
  preventUpdates,
  sheetType,
  allowPaymentMethod,
  allowedPaymentMethods,
  paymentMethod,
  currentUser: { userType },
  customerToggle,
  displayCustomerToggle = false,
  sheetViewType,
  hasPermission,
  rates,
  inPreApprovalState,
  totalIntroductionPayment,
  funderIntroductionPayment,
  dealerIntroductionPayment,
  cofundedIntroductionPayment,
}) => {
  const { t } = useTranslation("scheduleSetup");

  const translatedPaymentMethods = useMemo(
    () =>
      paymentMethods.map((x) => ({
        text: t(`enums:paymentMethod:${x.value}`),
        value: x.value,
      })),
    [paymentMethods],
  );

  const readOnly = useMemo(
    () => sheetViewType !== SheetViewType.IpsAdmin,
    [sheetViewType],
  );

  const disableMasterTerms = useMemo(
    () =>
      sheetDetailsSaving ||
      (process.env.TRADE_AS_IPS &&
        (sheetType == SheetType.Rewrite ||
          sheetType == SheetType.RewriteNewMasterDocument)) ||
      (!inPreApprovalState && (readOnly || preventUpdates)),
    [sheetType],
  );

  const yesNoOpts = useMemo(
    () => [
      { value: true, text: "Yes" },
      { value: false, text: "No" },
    ],
    [],
  );

  if (!hasPermission(Targets.Sheet, Actions.ViewScheduleSetup)) {
    return null;
  }

  return (
    <div ref={reactRef} className="step-container">
      <SheetStep
        title={t("Schedule_Set_Up")}
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        displayPrevious={displayPrevious}
        displayNext={displayNext}
        displaySave={displaySave}
        collapsed={collapsed}
        toggleVisibility={toggleVisibility}
        calculating={calculating}
        sheetId={sheetId}
        displayCustomerToggle={displayCustomerToggle}
        customerToggle={customerToggle}
        className="rates-step"
      >
        <Form.Object field="rates" propagateUpdates="always">
          <div className="rates-step">
            <div className="rates-step-grid">
              <div className="rates-step-field-row">
                <div className="rates-step-field-group group-1">
                  <SheetTypeDropdownLookup
                    className="sheet-type"
                    field="sheetType"
                    label={t("Sheet_Type")}
                    placeholder={t("Sheet_Type_placeholder")}
                    showErrors
                    fluid
                    optionField="sheetType"
                    disabled={
                      sheetDetailsSaving ||
                      (!inPreApprovalState && (readOnly || preventUpdates))
                    }
                  />
                  <Form.Date
                    dateFormat="DD MMM YYYY"
                    local
                    field="applicableFromDate"
                    label={t("Applicable_From")}
                    showErrors
                    fluid
                    placeholder="&nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;/"
                    className="date-applicable-from"
                    disabled={readOnly || preventUpdates || sheetDetailsSaving}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                  <Form.Date
                    dateFormat="DD MMM YYYY"
                    local
                    field="expiryDate"
                    label={t("Expiry_Date")}
                    showErrors
                    fluid
                    placeholder="&nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;/"
                    className="date-applicable-from"
                    disabled={readOnly || preventUpdates || sheetDetailsSaving}
                  />
                  <Form.If condition={() => true}>
                    <FixedInput
                      label={
                        <FixedInputResponsiveLabel
                          label={t("Display_Settlement")}
                          className="sheet-radio-title green"
                        />
                      }
                      field="displaySettlement"
                      fluid
                      formatNumber
                      currency
                      className="medium"
                      placeholder={t("NIL")}
                      minAllowValue={0}
                      disabled={
                        sheetDetailsSaving ||
                        (!inPreApprovalState && (readOnly || preventUpdates))
                      }
                    />
                    <FixedString
                      field="customerPOReference"
                      label={
                        <FixedInputResponsiveLabel
                          label={t("Customer_PO_Reference")}
                          className="sheet-radio-title green"
                        />
                      }
                      fluid
                      className="sheet-radio-title"
                      disabled={
                        sheetDetailsSaving ||
                        (!inPreApprovalState && (readOnly || preventUpdates))
                      }
                    />
                  </Form.If>
                  {!process.env.TRADE_AS_IPS && (
                    <Form.Dropdown
                      options={yesNoOpts}
                      field="consumablesExclusive"
                      label={t("Consumables_Exclusive")}
                      disabled={
                        sheetDetailsSaving ||
                        (!inPreApprovalState && (readOnly || preventUpdates))
                      }
                    />
                  )}
                </div>
              </div>
              <div className="rates-step-field-row">
                <div className="rates-step-field-group group-7">
                  <Form.TextArea
                    field="masterAdditionalTerms"
                    label={
                      process.env.TRADE_AS_IPS === true
                        ? t("Master_Additional_Terms")
                        : t("Additional_Information")
                    }
                    placeholder={
                      process.env.TRADE_AS_IPS === true
                        ? t("Master_Additional_Terms_placeholder")
                        : t("Additional_Information_placeholder")
                    }
                    fluid
                    rows={2}
                    className="highlighted-input"
                    disabled={disableMasterTerms}
                  />
                </div>
                <div className="rates-step-field-group group-8">
                  <Form.TextArea
                    field="scheduleAdditionalTerms"
                    label={
                      process.env.TRADE_AS_IPS === true
                        ? t("Schedule_Additional_Terms")
                        : t("Additional_Information_To_Schedule")
                    }
                    placeholder={
                      process.env.TRADE_AS_IPS === true
                        ? t("Schedule_Additional_Terms_placeholder")
                        : t("Additional_Information_To_Schedule_placeholder")
                    }
                    fluid
                    rows={2}
                    className="highlighted-input"
                    disabled={
                      sheetDetailsSaving ||
                      (!inPreApprovalState && (readOnly || preventUpdates))
                    }
                  />
                </div>

                <div className="rates-step-field-group group-9">
                  <div>
                    <p>{t("Upgrade_Clause")}</p>
                  </div>
                  <div className="field-container">
                    <Form.Checkbox
                      field="upgradeClauseToggle"
                      disabled={
                        sheetDetailsSaving ||
                        (!inPreApprovalState && (readOnly || preventUpdates))
                      }
                    />
                    <Form.If
                      condition={({ fields }) => fields.upgradeClauseToggle}
                    >
                      <Form.TextArea
                        field="upgradeClause"
                        placeholder={t("Upgrade_Clause_placeholder")}
                        fluid
                        rows={2}
                        className="highlighted-input field"
                        disabled={
                          sheetDetailsSaving ||
                          (!inPreApprovalState && (readOnly || preventUpdates))
                        }
                      />
                    </Form.If>
                  </div>
                </div>
              </div>
              <div className="flex">
                <PermissionCheck
                  target={Targets.Sheet}
                  action={Actions.ViewPaymentMethod}
                >
                  <div className="rates-step-field-row">
                    <div className="rates-step-field-group group-4">
                      {userType === UserType.HQ || allowPaymentMethod ? (
                        <React.Fragment>
                          <div className="sheet-radio-title">
                            <p>{t("Payment_Method")}</p>
                          </div>
                          <div className="payment-method-radio">
                            <Form.RadioGroup
                              width={12}
                              inline={true}
                              options={
                                allowedPaymentMethods
                                  ? translatedPaymentMethods.filter((x) =>
                                      allowedPaymentMethods.includes(x.value),
                                    )
                                  : translatedPaymentMethods
                              }
                              field="paymentMethod"
                              className="required-field sheet-radio-group payment"
                              disabled={
                                readOnly || preventUpdates || sheetDetailsSaving
                              }
                            />
                          </div>
                        </React.Fragment>
                      ) : (
                        <Form.Input
                          label={t("Payment_Method")}
                          inline={true}
                          readOnly
                          renderReadOnly={() => (
                            <span>
                              {t(`enums:paymentMethod:${paymentMethod}`)}
                            </span>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </PermissionCheck>
                <div className=" introduction-payment-total flex">
                  <div className="label">{t("Introduction_Payment_Total")}</div>
                  {totalIntroductionPayment ? (
                    <Format.Number
                      value={totalIntroductionPayment}
                      decimalPlaces={2}
                      currency
                      format={process.env.CURRENCY_CODE}
                    />
                  ) : (
                    <span>{`${process.env.MAJOR_CURRENCY}${t("NIL")}`}</span>
                  )}
                </div>
              </div>
              <div className="rates-step-field-row">
                <div className="rates-step-field-group group-10">
                  <FmvSection
                    funderType={FunderType.Funder}
                    introductionPayment={funderIntroductionPayment}
                  />
                  <FmvSection
                    funderType={FunderType.Dealer}
                    introductionPayment={dealerIntroductionPayment}
                  />
                  <FmvSection
                    funderType={FunderType.CoFunded}
                    introductionPayment={cofundedIntroductionPayment}
                  />
                </div>
              </div>
            </div>
          </div>
        </Form.Object>
      </SheetStep>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { applicableFromDate, sheetType, paymentMethod } =
    getSheetRatesState(state);
  const capitalCalcCosts =
    getCalculationData(state)?.breakdown?.capitalCalcCosts;
  const totalIntroductionPayment = capitalCalcCosts?.introductionPayment;
  const funderIntroductionPayment = capitalCalcCosts?.itemCosts.filter(
    (x) => x.funderType == FunderType.Funder,
  )[0]?.introductionPayment;
  const dealerIntroductionPayment = capitalCalcCosts?.itemCosts.filter(
    (x) => x.funderType == FunderType.Dealer,
  )[0]?.introductionPayment;
  const cofundedIntroductionPayment = capitalCalcCosts?.itemCosts.filter(
    (x) => x.funderType == FunderType.CoFunded,
  )[0]?.introductionPayment;

  const {
    unlockAllowedTieredToner,
    unlockDisableMinVolsLogic,
    unlockIncludeUpgradeClause,
  } = getSheetIpsRatesState(state);
  const { calculating, sheetDetailsSaving, sheetViewType } =
    getSheetState(state);

  const { rates, preventUpdates, inPreApprovalState } =
    getSheetDataState(state);

  const { allowPaymentMethod, allowedPaymentMethods } =
    getSheetRestrictionsState(state) || {};

  return {
    applicableFromDate,
    unlockAllowedTieredToner,
    unlockDisableMinVolsLogic,
    unlockIncludeUpgradeClause,
    totalIntroductionPayment,
    funderIntroductionPayment,
    dealerIntroductionPayment,
    cofundedIntroductionPayment,
    calculating,
    sheetDetailsSaving,
    preventUpdates,
    sheetType,
    allowPaymentMethod,
    allowedPaymentMethods,
    paymentMethod,
    sheetViewType,
    rates,
    userArea: getAuthClaim(state, "userArea"),
    inPreApprovalState,
  };
};

export default compose(
  connect(mapStateToProps),
  withCurrentUser,
)(withPermissions(ScheduleSetupStep));
